import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Stack, Switch, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { TextCellWithTooltip } from "components/DataGridCell";
import { StatusChip } from "components/StatusChip";
import { EmailIntakeState } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useMemo } from "react";

import type { SenderListingRow } from "./SendersGrid";

import { exactTimestamp, facilityHintLabel, matchLabel, relativeReceived } from "./displayText";
import {
  conflictTooltip,
  INTAKE_STATE_DISPLAY,
  MATCH_FILTER_LABELS,
  MATCH_FILTER_VALUES,
} from "./sendersDisplay";

/**
 * Columns whose filter the resolver does not parse.
 *
 * The popover derives a control from every column it is not told to skip, so without
 * this it offered eight fields that silently did nothing — plus a second copy of
 * three that do, once under the column's header and once under a separately declared
 * key. Free text already matches the sender value and its label.
 */
export const SENDERS_EXCLUDE_FILTER_COLUMNS = [
  "value",
  "label",
  "facility",
  "conflicts",
  "lastReceivedAt",
  "documentCount",
  "updatedAt",
  "updatedBy",
  "createdBy",
];

/**
 * Who an entry is, in words: its label and its facility hint. Split out of the main
 * column list because the QA pass found both missing — the ticket's grid spec had
 * them, and a free-text search that matched on the label showed a row with no
 * visible reason for being there.
 */
function identityColumns(): OmnisearchGridColDef<SenderListingRow>[] {
  return [
    {
      // The human name of the entry ("DCI — Dallas dispatch"). Free-text search
      // matches on it, so without this column a search could return a row with no
      // visible reason for being there.
      field: "label",
      headerName: "Label",
      flex: 1.4,
      minWidth: 160,
      valueGetter: (_v, row) => row.sender.label ?? "",
      renderCell: ({ row }) =>
        row.sender.label ? (
          <TextCellWithTooltip>{row.sender.label}</TextCellWithTooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        ),
    },
    {
      // A hint for telling two vendor mailboxes apart, never used to route — manifests
      // bind to facilities by EPA ID downstream. Hidden by default; it earns its
      // width only for organizations with several sender entries.
      field: "facility",
      headerName: "Facility",
      flex: 1.4,
      minWidth: 160,
      sortable: false,
      valueGetter: (_v, row) => facilityHintLabel(row.sender.facility),
      renderCell: ({ row }) => {
        const hint = facilityHintLabel(row.sender.facility);
        return hint ? (
          <TextCellWithTooltip>{hint}</TextCellWithTooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        );
      },
    },
  ];
}

/**
 * Columns for the cross-tenant senders grid.
 *
 * Their own module because inlining them pushed the component past the
 * lines-per-function limit, and because a column set is the kind of thing you read on
 * its own when asking what the grid shows.
 */
export function useSenderColumns({
  onEdit,
  onToggle,
  onDelete,
}: {
  onEdit: (row: SenderListingRow) => void;
  onToggle: (row: SenderListingRow, enabled: boolean) => void;
  onDelete: (row: SenderListingRow) => void;
}): OmnisearchGridColDef<SenderListingRow>[] {
  return useMemo<OmnisearchGridColDef<SenderListingRow>[]>(
    () => [
      {
        field: "tenant",
        headerName: "Organization",
        flex: 1.3,
        minWidth: 140,
        filterKeyType: "multipleTenant",
        valueGetter: (_v, row) => row.tenant.name,
        renderCell: ({ row }) => <TextCellWithTooltip>{row.tenant.name}</TextCellWithTooltip>,
      },
      {
        field: "intakeState",
        // Sortable via the resolver, which orders on the two columns behind it.
        sortable: true,
        filterKeyType: "enum",
        enumValues: Object.values(EmailIntakeState),
        enumPresentationFunction: (value) =>
          INTAKE_STATE_DISPLAY[value as EmailIntakeState]?.label ?? value,
        // "Org intake", not "Intake": this is the organization's state, and it sits
        // next to a per-entry "Accepting mail" column. A disabled entry under a live
        // organization reads as a contradiction until the two are told apart.
        headerName: "Org intake",
        flex: 0.7,
        minWidth: 110,
        valueGetter: (_v, row) => INTAKE_STATE_DISPLAY[row.intakeState].label,
        // No tooltip, but note what "On" does and does not claim: the organization is
        // live and something is switched on, not that every entry routes. A contested
        // entry still shows On here and a conflict in the column beside it, which is
        // the pairing that tells the truth — an organization-level rollup of ambiguity
        // would duplicate a per-entry column that already reports it.
        renderCell: ({ row }) => {
          const { label, color } = INTAKE_STATE_DISPLAY[row.intakeState];
          return <StatusChip color={color} label={label} />;
        },
      },
      {
        field: "value",
        headerName: "Sender",
        flex: 1.8,
        minWidth: 190,
        valueGetter: (_v, row) => row.sender.value,
        renderCell: ({ row }) => <TextCellWithTooltip>{row.sender.value}</TextCellWithTooltip>,
      },
      ...identityColumns(),
      {
        field: "matchType",
        headerName: "Match",
        flex: 1,
        minWidth: 160,
        filterKeyType: "enum",
        enumValues: [...MATCH_FILTER_VALUES],
        enumPresentationFunction: (value) => MATCH_FILTER_LABELS[value] ?? value,
        valueGetter: (_v, row) => matchLabel(row.sender),
        // The label combines matchType with includeSubdomains, so ordering on
        // matchType alone interleaved "Domain" and "Domain + subdomains"
        // arbitrarily — the resolver orders on both. TextCellWithTooltip because a
        // plain value in a flex cell clips mid-word instead of ellipsizing.
        renderCell: ({ row }) => (
          <TextCellWithTooltip>{matchLabel(row.sender)}</TextCellWithTooltip>
        ),
      },
      {
        field: "enabled",
        headerName: "Accepting mail",
        width: 150,
        filterKeyType: "boolean",
        valueGetter: (_v, row) => (row.sender.enabled ? "Yes" : "No"),
        // Full-size switch with the state spelled out beside it. A bare small switch
        // in a grid column reads as decoration, and this one decides whether a
        // customer's mail gets in — the word is what makes a disabled row obvious
        // while scanning, and amber is the same language the status chips use.
        renderCell: ({ row }) => (
          <Tooltip
            title={
              row.sender.enabled
                ? "Accepting mail. Turn off to stop it without losing the entry or its history."
                : "Not accepting mail. Refused outright rather than routed to whoever holds a broader entry."
            }
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Switch
                checked={row.sender.enabled}
                slotProps={{ input: { "aria-label": `Accept mail from ${row.sender.value}` } }}
                onChange={(event) => onToggle(row, event.target.checked)}
              />
              <Typography
                variant="body2"
                color={row.sender.enabled ? "text.primary" : "warning.main"}
                fontWeight={row.sender.enabled ? undefined : 600}
              >
                {row.sender.enabled ? "Yes" : "No"}
              </Typography>
            </Stack>
          </Tooltip>
        ),
      },
      {
        // The reason a cross-tenant view of the allowlist is worth having.
        field: "conflicts",
        headerName: "Conflicts",
        flex: 1,
        minWidth: 125,
        sortable: false,
        filterable: false,
        valueGetter: (_v, row) => row.conflicts.map((c) => c.tenantName).join(", "),
        renderCell: ({ row }) => {
          // Blank rather than a dash, matching how empty cells read elsewhere.
          if (row.conflicts.length === 0) return null;
          return (
            <Tooltip title={conflictTooltip(row.conflicts)}>
              <span>
                {/* "Conflicting" rather than "claiming": an overlapping entry holds a
                    different value, so it is not claiming this one. */}
                <StatusChip color="error" label={`${row.conflicts.length} conflicting`} />
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: "lastReceivedAt",
        headerName: "Last Received",
        flex: 1,
        minWidth: 130,
        // Sortable, though there is no column behind it: the resolver computes the
        // newest receivedAt across the entry's mail and pages on that. Entries that
        // have never received anything sort last in both directions, since "never" is
        // the answer to "what is vestigial".
        valueGetter: (_v, row) => row.sender.lastReceivedAt,
        renderCell: ({ row }) =>
          row.sender.lastReceivedAt ? (
            <Tooltip title={exactTimestamp(row.sender.lastReceivedAt)}>
              <span>{relativeReceived(row.sender.lastReceivedAt)}</span>
            </Tooltip>
          ) : (
            relativeReceived(row.sender.lastReceivedAt)
          ),
      },
      {
        field: "documentCount",
        headerName: "Documents",
        // Sortable the same way Last Received is — computed and paged by the
        // resolver rather than ordered by the database.
        flex: 0.7,
        minWidth: 110,
        valueGetter: (_v, row) => row.sender.documentCount,
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        flex: 1,
        minWidth: 130,
        valueGetter: (_v, row) => row.sender.updatedAt,
        renderCell: ({ row }) => relativeReceived(row.sender.updatedAt),
      },
      {
        field: "updatedBy",
        headerName: "Updated By",
        flex: 1,
        minWidth: 150,
        valueGetter: (_v, row) => row.sender.updatedBy,
      },
      {
        field: "createdBy",
        headerName: "Created By",
        flex: 1,
        minWidth: 150,
        valueGetter: (_v, row) => row.sender.createdBy,
      },
      {
        field: "actions",
        // `type: "actions"` is what earns the shared grid's hover reveal — its sx
        // fades in the `role="menu"` container MUI renders for this column type. A
        // hand-rolled renderCell sits outside that rule and stays visible.
        type: "actions",
        headerName: "",
        width: 60,
        // Behind one overflow button: two bare icons beside the Enabled switch read
        // as a third and fourth control on the row rather than as things you go
        // looking for.
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit"
            showInMenu
            icon={<Edit fontSize="small" />}
            label="Edit"
            onClick={() => onEdit(row)}
          />,
          <GridActionsCellItem
            key="delete"
            showInMenu
            icon={<Delete fontSize="small" />}
            label="Remove"
            onClick={() => onDelete(row)}
          />,
        ],
      },
    ],
    [onEdit, onToggle, onDelete]
  );
}
